<template>
  <b-card
    no-body
    class="modern-card event-card mb-3"
    :class="cardClass"
  >
    <b-card-body class="modern-card-body">
      <div
        v-if="content.is_deleted || localDeleted"
        class="tombstone"
      >
        <i class="fa fa-trash mr-2" />
        <span v-if="content.is_deleted">
          Esta publicación fue eliminada por <strong>{{ content.deleted_by_name }}</strong>
        </span>
        <span v-else>Esta publicación fue eliminada</span>
      </div>

      <template v-else>
        <div class="card-header-row mb-3">
          <div class="post-meta">
            <b-avatar
              :variant="content.isCheckIn ? 'success' : 'primary'"
              :text="avatarText"
              class="mr-3"
            />
            <div>
              <div class="post-author">
                {{ content.name }}
              </div>
              <div class="post-store">
                {{ content.retail_name_store_name || 'Tienda sin nombre' }}
              </div>
              <div class="post-date">
                {{ formattedDate }}
              </div>
            </div>
          </div>

          <div class="event-right">
            <div class="event-right-header">
              <span
                class="event-badge"
                :class="badgeClass"
              >
                <i
                  class="mr-1"
                  :class="badgeIcon"
                />
                {{ badgeLabel }}
              </span>
              <feed-delete-button
                :post-type="content.post_type"
                :store-name="content.retail_name_store_name"
                :deleting="deleting"
                @delete="handleDelete"
              />
            </div>
            <div
              v-if="content.checked_in_at"
              class="event-meta"
            >
              <span class="event-meta-item">
                <i class="fa fa-clock-o mr-1" />
                Llegó {{ checkedInTime }}
              </span>
              <span
                v-if="content.visitDuration"
                class="event-meta-item duration-badge"
              >
                <i class="fa fa-hourglass-half mr-1" />
                {{ content.visitDuration }} en tienda
              </span>
            </div>
          </div>
        </div>

        <div
          v-if="isMounted && content.hasCoordinates"
          class="map-container"
        >
          <GmapMap
            :center="mapCenter"
            :zoom="15"
            :options="mapOptions"
            class="event-map"
          >
            <GmapMarker :position="mapCenter" />
          </GmapMap>

          <button
            class="expand-map-btn"
            @click="showFullscreen = true"
          >
            <i class="fa fa-expand" />
          </button>
        </div>
        <div
          v-else-if="!content.hasCoordinates"
          class="map-placeholder"
        >
          <i class="fa fa-map-marker fa-lg mr-2" />
          <span>Ubicación no disponible</span>
        </div>
      </template>
    </b-card-body>

    <!-- Fullscreen modal -->
    <b-modal
      v-model="showFullscreen"
      size="xl"
      hide-footer
      :title="`${content.name} · ${content.retail_name_store_name || 'Tienda sin nombre'}`"
      body-class="p-0"
      modal-class="map-fullscreen-modal"
    >
      <GmapMap
        v-if="showFullscreen"
        :center="mapCenter"
        :zoom="15"
        :options="mapOptionsFullscreen"
        class="fullscreen-map"
      >
        <GmapMarker :position="mapCenter" />
      </GmapMap>
    </b-modal>
  </b-card>
</template>

<script>
import { computed, ref, onMounted } from '@vue/composition-api'
import { deleteCheckInOut } from '../Services'
import FeedDeleteButton from './FeedDeleteButton.vue'

export default {
  name: 'CheckEventCard',
  components: { FeedDeleteButton },
  props: {
    content: {
      type: Object,
      required: true
    }
  },
  setup (props, context) {
    const isMounted = ref(false)
    const showFullscreen = ref(false)
    const deleting = ref(false)
    const localDeleted = ref(false)

    onMounted(() => { isMounted.value = true })

    const avatarText = computed(() =>
      props.content.name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase()
    )

    const formattedDate = computed(() =>
      context.root.$moment(props.content.created_at).format('D MMMM YYYY, h:mm a')
    )

    const checkedInTime = computed(() =>
      props.content.checked_in_at
        ? context.root.$moment(props.content.checked_in_at).format('h:mm a')
        : null
    )

    const cardClass = computed(() =>
      props.content.isCheckIn ? 'check-in-card' : 'check-out-card'
    )

    const badgeClass = computed(() =>
      props.content.isCheckIn ? 'badge-check-in' : 'badge-check-out'
    )

    const badgeIcon = computed(() =>
      props.content.isCheckIn ? 'fa fa-sign-in' : 'fa fa-flag-checkered'
    )

    const badgeLabel = computed(() =>
      props.content.isCheckIn ? 'Entrada a tienda' : 'Visita completada'
    )

    const mapCenter = computed(() => ({
      lat: props.content.latitude,
      lng: props.content.longitude
    }))

    const mapOptions = {
      zoomControl: false,
      mapTypeControl: false,
      scaleControl: false,
      streetViewControl: false,
      rotateControl: false,
      fullscreenControl: false,
      disableDefaultUi: true
    }

    const mapOptionsFullscreen = {
      zoomControl: true,
      mapTypeControl: false,
      scaleControl: false,
      streetViewControl: true,
      rotateControl: false,
      fullscreenControl: false
    }

    const handleDelete = async (target) => {
      deleting.value = true
      localDeleted.value = true

      const checkInId = props.content.check_in_id ?? props.content.id

      try {
        await deleteCheckInOut(checkInId, target)
        if (target === 'check_out') {
          context.emit('deleted', props.content.id)
        } else {
          context.emit('check-in-deleted', checkInId)
        }
      } catch {
        localDeleted.value = false
      } finally {
        deleting.value = false
      }
    }

    return {
      isMounted,
      showFullscreen,
      deleting,
      localDeleted,
      avatarText,
      formattedDate,
      checkedInTime,
      cardClass,
      badgeClass,
      badgeIcon,
      badgeLabel,
      mapCenter,
      mapOptions,
      mapOptionsFullscreen,
      handleDelete
    }
  }
}
</script>

<style scoped>
.modern-card {
  border: none;
  border-radius: 10px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.07);
  overflow: hidden;
}

.modern-card-body {
  padding: 1rem;
}

.check-in-card {
  border-left: 4px solid #48bb78;
}

.check-out-card {
  border-left: 4px solid #4299e1;
}

.card-header-row {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
}

.post-meta {
  display: flex;
  align-items: center;
}

.post-author {
  font-size: 0.8rem;
  font-weight: 600;
  color: #2d3748;
}

.post-store {
  font-size: 0.75rem;
  color: #718096;
}

.post-date {
  font-size: 0.7rem;
  color: #a0aec0;
}

.event-right {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 0.35rem;
  flex-shrink: 0;
}

.event-right-header {
  display: flex;
  align-items: center;
  gap: 0.4rem;
}

.event-badge {
  display: inline-flex;
  align-items: center;
  padding: 0.3rem 0.65rem;
  border-radius: 20px;
  font-size: 0.72rem;
  font-weight: 600;
  white-space: nowrap;
}

.event-meta {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 0.2rem;
}

.event-meta-item {
  font-size: 0.68rem;
  color: #718096;
  white-space: nowrap;
}

.duration-badge {
  color: #4299e1;
  font-weight: 600;
}

.badge-check-in {
  background: rgba(72, 187, 120, 0.12);
  color: #276749;
  border: 1px solid rgba(72, 187, 120, 0.3);
}

.badge-check-out {
  background: rgba(66, 153, 225, 0.12);
  color: #2b6cb0;
  border: 1px solid rgba(66, 153, 225, 0.3);
}

.map-container {
  position: relative;
  border-radius: 8px;
  overflow: hidden;
  margin-top: 0.5rem;
}

.event-map {
  width: 100%;
  height: 200px;
}

.expand-map-btn {
  position: absolute;
  bottom: 0.5rem;
  right: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  background: white;
  border: none;
  border-radius: 4px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.3);
  color: #4a5568;
  cursor: pointer;
  font-size: 0.75rem;
  transition: background 0.15s ease;
  z-index: 10;
}

.expand-map-btn:hover {
  background: #f7fafc;
  color: #2d3748;
}

.fullscreen-map {
  width: 100%;
  height: 70vh;
}

.map-placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 80px;
  background: #f8fafc;
  border: 1px dashed #e2e8f0;
  border-radius: 8px;
  color: #a0aec0;
  font-size: 0.8rem;
  margin-top: 0.5rem;
}

.tombstone {
  display: flex;
  align-items: center;
  padding: 0.5rem 0.75rem;
  background: #f8fafc;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
  color: #718096;
  font-size: 0.8rem;
  font-style: italic;
}

</style>

<style>
.map-fullscreen-modal .modal-body {
  padding: 0 !important;
}

.map-fullscreen-modal .modal-content {
  border: none;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15);
}
</style>



// WEBPACK FOOTER //
// src/app/news_feed/components/CheckEventCard.vue